import {Brand} from './types';
import parseDate from 'date-fns/fp/parse'
import parseISODate from 'date-fns/fp/parseISO';
import parseJson from 'date-fns/fp/parseJSON';
import formatDateTime from 'date-fns/fp/format';
import formatISODate from 'date-fns/fp/formatISOWithOptions';
import addMinutes from 'date-fns/fp/addMinutes';
import o from 'ramda/src/o';

function isValidDate(date: Date): boolean {
  return !isNaN(date.getTime());
}

//#region Types

export type FormDateRange = {
  /** Format: M/D/YYYY */
  firstDate: string,
  /** Format: M/D/YYYY */
  lastDate: string
}

/** A date that has been formatted for displaying to users */
export type DisplayDate = Brand<string, 'DisplayDate'>

export type LocalDate = {
  readonly year: number,
  readonly month: number,
  readonly day: number
}

export type DisplayDateRange = {
  firstDate: DisplayDate,
  lastDate: DisplayDate
}

/** Alias for the native JS Date object that clearly indicates a time component is involved. */
export type DateTime = Date;

/**
 * Alias for the JS number object that clearly indicates
 * that it represents an instant as ticks elapsed since epoch
 */
export type InstantNum = Brand<number, 'DateTimeNum'>;

export type JsonInstant = Brand<string, 'JsonInstant'>

//#endregion

//#region Methods

export const stripISOTimeComponent = o(
  formatISODate({representation: 'date'}),
  parseISODate
);

export function parseEnUsDate(date: string|undefined): LocalDate|undefined {
  const d = parseDate(new Date(), 'M/d/yyyy', date as string);
  return isValidDate(d)
  ? {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate()
    }
  : undefined;
}

export function parseOptionalIsoDate(date: string|undefined): LocalDate|undefined {
  return date
  ? parseIsoDate(date)
  : undefined;
}

export const parseOptionalJsonDate = parseOptionalIsoDate;

/**
 *
 * @param date
 * @throws {RangeError} if unable to parse the date
 */
export function parseIsoDate(date?: string): LocalDate {
  const d = parseDate(new Date(), 'yyyy-MM-dd', date as string);

  if (!isValidDate(d)) {
    throw new RangeError(`Invalid ISO date '${date}'`);
  }

  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate()
  };
}

// istanbul ignore next
export const parseJsonDate = parseIsoDate;

// istanbul ignore next
export const parseSqlDate = parseIsoDate;

export function displayDate(date?: LocalDate): string {
  if (!date) {return '';}

  const {year, month, day} = date;
  const dt = new Date(year, month - 1, day);
  return formatDateTime('P', dt);
}

export function displayDateTime(date?: DateTime): string {
  if (!date) {return '';}

  try {
    return formatDateTime('P p', date);
  } catch (err: any) {
    if (err.message === 'Invalid time value') {
      return '{invalid}'
    }
    else {
      throw err;
    }
  }
}

export function isoDateFormat(date?: LocalDate): string {
  function twoIntegerDigits(number: number): string {
    return number.toLocaleString('en-us', {
      minimumIntegerDigits: 2
    });
  }


  if (date) {
    const {year, month, day} = date;
    return `${year}-${twoIntegerDigits(month)}-${twoIntegerDigits(day)}`;
  }
  else {
    return '';
  }
}

// istanbul ignore next
export const jsonDateFormat = isoDateFormat;

// istanbul ignore next
export const sqlDateFormat = isoDateFormat;

export function sqlDateTimeFormat(dateTime?: DateTime): string {
  if (dateTime) {
    return formatDateTime('yyyy-MM-dd kk:mm:ss', dateTime);
  }
  else {
    return '';
  }
}

export function jsonDateTimeFormat(instant?: DateTime): JsonInstant|'' {
  return instant
    ? formatISODate({}, instant) as JsonInstant
    : '';
}

/** Formats as a SQL date time with milliseconds precision */
export function sqlTimestampFormat(instant?: DateTime): string {
  return instant
    ? formatDateTime('yyyy-MM-dd HH:mm:ss.SSS', instant)
    : '';
}

// istanbul ignore next
/**
 * Parse a ISO8601-like string into a Date object.
 * Invalid strings will result in a Date object with a NaN time
 * value.
 *
 * It is recommended that the timezone be explicitly specified in
 * the input string. Failure to do so will more likely than not
 * result in confusing and undesired program behavior.
 *
 * [Supported formats]@link https://date-fns.org/v2.21.1/docs/parseJSON
 */
export function parseJsonDateTime(dateTime?: string): DateTime|undefined {
  return dateTime
    ? parseJson(dateTime)
    : undefined;
}

/**
 * Parse SQL format date/times into a native JavaScript Date object.
 * The SQL date/time is interpreted as representing an instant in the
 * local timezone of the computer doing the parsing.
 *
 * Invalid strings will result in a Date object with a NaN time
 * value.
 */
export function parseSqlDateTime(str: string): DateTime {
  const utcInstant = parseJson(str);
  const res = addMinutes(utcInstant.getTimezoneOffset(), utcInstant);
  return res;
}

// istanbul ignore next
export const displayJsonDate = o(displayDate, parseJsonDate);

export const displayJsonDateTime = o(displayDateTime, parseJsonDateTime);

export function monthAndDay(date?: number|Date): string {
  return date ? formatDateTime('MMdd', date) : '{undefined}';
}

//#endregion
